import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { marked } from "marked";
import parse, { domToReact, Element, type DOMNode, type HTMLReactParserOptions } from "html-react-parser";
import { Alert, Card, ResponsiveGrid } from "@marekl/adminer/components";
import { isolateTags } from "../../lib/customPages";
import styles from "./CustomPageContent.module.css";

export interface CustomPageContentProps {
  /** Markdown with raw HTML passed through. */
  body: string;
  /**
   * Run the page's <script> elements. The editor's preview turns this off,
   * so a script does not re-run on every keystroke.
   */
  runScripts?: boolean;
}

interface PageScript {
  attrs: Record<string, string>;
  text: string;
}

const ALERT_VARIANTS = ["info", "warn", "err", "ok"] as const;
type AlertVariant = (typeof ALERT_VARIANTS)[number];

function gridColumns(v: string | undefined): 2 | 3 | 4 {
  return v === "3" ? 3 : v === "4" ? 4 : 2;
}


/**
 * Renders a stránka: Markdown, with any HTML in it, <script> and <style>
 * included, put into the page AS WRITTEN. Nothing is sanitized: the
 * people who may write a page are trusted like the administrator
 * (README § Stránky).
 *
 * Three tags become the library's own components, so a page looks like the
 * rest of the app: <cmf-card title subtitle>, <cmf-grid columns="2|3|4">
 * and <cmf-alert variant title>, with Markdown inside, however the author
 * spaces them (isolateTags).
 *
 * Scripts cannot go through React, which never executes a <script> it
 * renders; they are pulled out of the tree and appended as fresh elements
 * once it is mounted. Under StrictMode that happens twice in development
 * (mount, cleanup, mount), never in production; the cleanup is what keeps
 * the second run from doubling the elements.
 */
export function CustomPageContent({ body, runScripts = true }: CustomPageContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  const { tree, scripts } = useMemo(() => {
    const found: PageScript[] = [];
    const options: HTMLReactParserOptions = {
      replace(node) {
        if (!(node instanceof Element)) return undefined;
        const a = node.attribs;
        const inner = (): ReactNode => domToReact(node.children as DOMNode[], options);
        switch (node.name) {
          case "script":
            found.push({
              attrs: a,
              text: node.children.map((c) => ("data" in c ? String(c.data) : "")).join(""),
            });
            return <></>;
          case "cmf-card":
            return (
              <Card>
                {a.title && <Card.Header title={a.title} subtitle={a.subtitle} />}
                <Card.Content>
                  <div className={styles.cardBody}>{inner()}</div>
                </Card.Content>
              </Card>
            );
          case "cmf-grid":
            return <ResponsiveGrid columns={gridColumns(a.columns)}>{inner()}</ResponsiveGrid>;
          case "cmf-alert": {
            const variant: AlertVariant = ALERT_VARIANTS.includes(a.variant as AlertVariant)
              ? (a.variant as AlertVariant)
              : "info";
            return (
              <Alert variant={variant} title={a.title}>
                {inner()}
              </Alert>
            );
          }
          default:
            return undefined;
        }
      },
    };
    const html = marked.parse(isolateTags(body), { async: false });
    return { tree: parse(html, options), scripts: found };
  }, [body]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !runScripts || scripts.length === 0) return;
    // One at a time, as the parser would run them: an inline script runs
    // the moment it is inserted, so „<script src=widget.js>" followed by
    // „<script>Widget.init()</script>" needs the first one LOADED before the
    // second goes in. async=false orders src scripts only among themselves.
    let cancelled = false;
    const added: HTMLScriptElement[] = [];
    void (async () => {
      for (const s of scripts) {
        if (cancelled) return;
        const script = document.createElement("script");
        for (const [k, v] of Object.entries(s.attrs)) script.setAttribute(k, v);
        script.text = s.text;
        const loaded = s.attrs.src
          ? new Promise<void>((resolve) => {
              script.onload = () => resolve();
              // A failed src must not stop the scripts after it.
              script.onerror = () => resolve();
            })
          : null;
        el.appendChild(script);
        added.push(script);
        if (loaded) await loaded;
      }
    })();
    return () => {
      cancelled = true;
      added.forEach((s) => s.remove());
    };
  }, [scripts, runScripts]);

  return (
    <div ref={ref} className={styles.content}>
      {tree}
    </div>
  );
}
